// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { diffJson, diffWords, type Change } from 'diff';

import { csvRow } from './ingest/csv';
import { renderHeadline, type RunResult } from './pipeline';
import type { Config, Summary } from './types';
import { LIMITS_COPY } from './copy';

export const VERSION = '1.0.0';

export interface ExportContext {
  readonly nameA: string;
  readonly nameB: string;
  readonly dialect: string;
  readonly config: Config;
  readonly generatedAt: string;
}

/* ── regressions.jsonl ───────────────────────────────────────────────────── */

export interface JsonlOptions {
  /** Which run's record to emit. B by default — that is the run that broke. */
  readonly side: 'a' | 'b';
  /**
   * Strict passthrough emits the source line byte-identically with NOTHING added, and the metadata
   * goes to a sidecar where line n describes line n. This resolves the real tension between
   * "re-runnable in the original shape" and "with the failing assertion attached" rather than
   * pretending one of them does not exist.
   */
  readonly strict: boolean;
}

export interface JsonlArtefact {
  readonly jsonl: string;
  readonly meta: string | null;
}

export function buildRegressionsJsonl(
  result: RunResult,
  ctx: ExportContext,
  opts: JsonlOptions,
): JsonlArtefact {
  const rows = result.cases.filter((c) => c.verdict === 'regression' || c.verdict === 'errored');
  const lines: string[] = [];
  const metaLines: string[] = [];

  for (const c of rows) {
    const row = opts.side === 'a' ? c.pair.a : c.pair.b;
    if (!row) continue;
    const meta = {
      verdict: c.verdict,
      reason: c.headlineReason,
      scorer: c.headlineScorer,
      caseId: c.id,
      fanout: c.fanoutLabel,
      joinMethod: c.pair.method,
      joinConfidence: c.pair.confidence,
      joinScore: c.pair.score,
      trajectory: c.trajectory
        ? {
            alignment: c.trajectory.alignment,
            firstDivergent: c.trajectory.firstDivergent,
            stepsA: c.trajectory.stepsA,
            stepsB: c.trajectory.stepsB,
          }
        : null,
      runA: ctx.nameA,
      runB: ctx.nameB,
      backslideVersion: VERSION,
    };

    if (opts.strict) {
      // Byte-identical. Not re-serialised: JSON.parse + JSON.stringify turns 12345678901234567890
      // into a rounded float, 1.0 into 1, and silently drops duplicate keys.
      lines.push(row.rawText);
      metaLines.push(JSON.stringify(meta));
    } else {
      let obj: unknown;
      try {
        obj = JSON.parse(row.rawText);
      } catch {
        obj = { output: row.outputText };
      }
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        lines.push(JSON.stringify({ ...(obj as Record<string, unknown>), _backslide: meta }));
      } else {
        lines.push(JSON.stringify({ value: obj, _backslide: meta }));
      }
    }
  }

  return {
    jsonl: lines.length > 0 ? `${lines.join('\n')}\n` : '',
    meta: opts.strict ? (metaLines.length > 0 ? `${metaLines.join('\n')}\n` : '') : null,
  };
}

/* ── regression.csv ──────────────────────────────────────────────────────── */

export const CSV_COLUMNS = [
  'case_id',
  'fanout',
  'verdict',
  'headline_reason',
  'scorer',
  'join_method',
  'join_confidence',
  'join_score',
  'steps_a',
  'steps_b',
  'alignment',
  'first_divergent_step',
  'inserted_steps',
  'deleted_steps',
  'mismatched_steps',
  'tokens_a',
  'tokens_b',
  'token_delta',
  'cost_a',
  'cost_b',
  'cost_delta',
  'latency_ms_a',
  'latency_ms_b',
  'latency_delta_ms',
  'edit_distance',
  'len_a',
  'len_b',
] as const;

const delta = (a: number | null | undefined, b: number | null | undefined): number | null =>
  a === null || a === undefined || b === null || b === undefined ? null : b - a;

export function buildRegressionCsv(result: RunResult, includeUnchanged: boolean): string {
  const rows = result.cases.filter((c) => includeUnchanged || c.verdict !== 'unchanged');
  // UTF-8 BOM so Excel reads it as UTF-8 rather than the local codepage.
  let out = '﻿' + csvRow([...CSV_COLUMNS]);
  for (const c of rows) {
    const a = c.pair.a;
    const b = c.pair.b;
    const t = c.trajectory;
    out += csvRow([
      c.id,
      c.fanoutLabel,
      c.verdict,
      c.headlineReason,
      c.headlineScorer ?? '',
      c.pair.method,
      c.pair.confidence,
      c.pair.score === null ? '' : c.pair.score.toFixed(4),
      t?.stepsA ?? '',
      t?.stepsB ?? '',
      t?.alignment ?? '',
      t?.firstDivergent ?? '',
      t?.inserted ?? '',
      t?.deleted ?? '',
      t?.mismatched ?? '',
      a?.tokensOut ?? '',
      b?.tokensOut ?? '',
      delta(a?.tokensOut, b?.tokensOut) ?? '',
      a?.cost ?? '',
      b?.cost ?? '',
      delta(a?.cost, b?.cost) ?? '',
      a?.latencyMs ?? '',
      b?.latencyMs ?? '',
      delta(a?.latencyMs, b?.latencyMs) ?? '',
      c.editDistance ?? '',
      a ? a.outputText.length : '',
      b ? b.outputText.length : '',
    ]);
  }
  return out;
}

/* ── the diff view ───────────────────────────────────────────────────────── */

/**
 * `diffJson` must be handed PARSED OBJECTS, not the output strings.
 *
 * Measured: `diffJson({a:1,b:2}, {b:2,a:1})` reports 0 hunks, which is correct — key order is not
 * a change. `diffJson('{"a":1,"b":2}', '{"b":2,"a":1}')` reports 2, because on strings it silently
 * degrades to a line diff. Since every adapter stores `outputText` as a string, passing it straight
 * through would produce noise on exactly the whitespace-reflow and key-reorder cases that are the
 * whole reason to prefer a JSON diff.
 */
export function buildDiff(aText: string, bText: string): Change[] {
  try {
    const a = JSON.parse(aText);
    const b = JSON.parse(bText);
    return diffJson(a, b);
  } catch {
    return diffWords(aText, bText);
  }
}

/* ── regression-report.md — the PR artefact ──────────────────────────────── */

/**
 * Markdown, not HTML, for the PR.
 *
 * GitHub's comment sanitiser allows neither the `style` attribute nor the `<style>` element, so a
 * self-contained styled HTML report pasted into a pull request loses every hairline, colour and
 * monospace figure. The HTML export still exists for a browser or an email; this is the one that
 * survives the place it is named after.
 */
export function buildReportMarkdown(result: RunResult, ctx: ExportContext): string {
  const h = renderHeadline(result.summary);
  const s = result.summary;
  const L: string[] = [];

  L.push(`## ${h.primary}`);
  if (h.secondary) L.push('', `**${h.secondary}**`);
  L.push('');
  L.push(`\`${ctx.nameA}\` → \`${ctx.nameB}\` · ${ctx.dialect} · ${ctx.generatedAt}`);
  L.push('');
  L.push(
    `${s.cases} cases · **${s.regressed} regressed** · ${s.errored} errored · ${s.diverged} diverged · ` +
      `${s.fixed} fixed · ${s.unchanged} unchanged · ${s.dropped} dropped · ${s.added} added`,
  );
  L.push('');
  L.push(`Joined on ${s.join.keyFunction} — ${s.join.exact} exact, ${s.join.similarity} by similarity, ${s.join.ambiguous} ambiguous.`);
  L.push('');

  const flips = Object.entries(s.flipsByScorer).filter(([, n]) => n > 0);
  if (flips.length > 0) {
    L.push('| what changed | rows |');
    L.push('| --- | ---: |');
    for (const [id, n] of flips.sort((x, y) => y[1] - x[1])) L.push(`| ${id} | ${n} |`);
    L.push('');
    L.push(
      '_Each row is counted once, under the first thing that explains it. Errors are listed ' +
        'separately because the fix for an error is to re-run, not to revert._',
    );
    L.push('');
  }

  const regressions = result.cases.filter((c) => c.verdict === 'regression');
  if (regressions.length > 0) {
    L.push('### Regressions');
    L.push('');
    for (const c of regressions.slice(0, 200)) {
      L.push(`<details><summary><code>${escapeMd(c.id)}</code> — ${escapeMd(c.headlineReason)}</summary>`);
      L.push('');
      if (c.trajectory && c.trajectory.alignment) {
        L.push(`Trajectory: \`${c.trajectory.alignment}\` (\`=\` same · \`~\` same tool, different arguments · \`X\` replaced · \`+\` inserted · \`-\` vanished)`);
        L.push('');
      }
      L.push('```diff');
      for (const part of buildDiff(c.pair.a?.outputText ?? '', c.pair.b?.outputText ?? '')) {
        if (!part.added && !part.removed) continue;
        const mark = part.added ? '+' : '-';
        for (const line of part.value.split('\n')) {
          if (line.trim() === '') continue;
          L.push(`${mark} ${line.slice(0, 400)}`);
        }
      }
      L.push('```');
      L.push('');
      L.push('</details>');
      L.push('');
    }
    if (regressions.length > 200) L.push(`_…and ${regressions.length - 200} more; see regression.csv._`, '');
  }

  const diverged = result.cases.filter((c) => c.verdict === 'diverged' || c.verdict === 'diverged-args');
  if (diverged.length > 0) {
    L.push('### Diverged, but nothing broke');
    L.push('');
    for (const c of diverged.slice(0, 50)) L.push(`- \`${escapeMd(c.id)}\` — ${escapeMd(c.headlineReason)}`);
    L.push('');
  }

  if (s.notes.length > 0) {
    L.push('### Notes');
    L.push('');
    for (const n of s.notes) L.push(`- ${escapeMd(n)}`);
    L.push('');
  }

  L.push('### What this cannot tell you');
  L.push('');
  for (const line of LIMITS_COPY) L.push(`- ${line.replace(/<[^>]+>/g, '')}`);
  L.push('');
  L.push(`_Generated by [Backslide](https://backslide.benrichardson.dev) ${VERSION}, entirely in the browser._`);
  return L.join('\n');
}

function escapeMd(s: string): string {
  return s.replace(/([|<>])/g, '\\$1');
}

/* ── diff-report.html ────────────────────────────────────────────────────── */

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Self-contained: no scripts, no external stylesheets, no fonts, no images, no fetch. A test
 * asserts there is no `http://`, `https://`, `<script` or `src=` anywhere in the output.
 */
export function buildReportHtml(result: RunResult, ctx: ExportContext): string {
  const h = renderHeadline(result.summary);
  const s: Summary = result.summary;
  const parts: string[] = [];

  parts.push('<!doctype html><html lang="en"><head><meta charset="utf-8">');
  parts.push('<meta name="viewport" content="width=device-width,initial-scale=1">');
  parts.push(`<title>Backslide report — ${esc(ctx.nameA)} vs ${esc(ctx.nameB)}</title>`);
  parts.push(
    '<style>' +
      ':root{color-scheme:light dark;--fg:#111;--dim:#666;--line:#ddd;--bad:#a3261e;--ok:#5b3fbf;--bg:#fff}' +
      '@media(prefers-color-scheme:dark){:root{--fg:#e8e8ef;--dim:#9a99ad;--line:#2a2a35;--bad:#f08a80;--ok:#a78bfa;--bg:#0b0d12}}' +
      'body{background:var(--bg);color:var(--fg);font:15px/1.55 ui-sans-serif,-apple-system,Segoe UI,Roboto,sans-serif;margin:0;padding:2rem 1.25rem;max-width:60rem;margin-inline:auto}' +
      'h1{font-size:1.9rem;letter-spacing:-.02em;margin:0 0 .25rem}h2{font-size:.8rem;text-transform:uppercase;letter-spacing:.07em;color:var(--dim);margin:2.2rem 0 .6rem;font-weight:600}' +
      '.sub{font-size:1.05rem;color:var(--dim);margin:0 0 1.2rem}' +
      '.meta{font:12px ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--dim);border-top:1px solid var(--line);border-bottom:1px solid var(--line);padding:.6rem 0;margin-bottom:1.4rem}' +
      'table{border-collapse:collapse;width:100%;font-size:.85rem}th,td{text-align:left;padding:.4rem .6rem;border-bottom:1px solid var(--line)}th{color:var(--dim);font-weight:500;font-size:.72rem;text-transform:uppercase;letter-spacing:.05em}' +
      'td.n{text-align:right;font-variant-numeric:tabular-nums}' +
      '.case{border-left:2px solid var(--line);padding:.5rem 0 .5rem .9rem;margin:.9rem 0}' +
      '.case.bad{border-left-color:var(--bad)}.case.ok{border-left-color:var(--ok)}' +
      '.id{font:12px ui-monospace,Menlo,monospace}.why{color:var(--dim);font-size:.87rem}' +
      'pre{font:12px/1.5 ui-monospace,Menlo,monospace;overflow-x:auto;border:1px solid var(--line);padding:.6rem;border-radius:4px;white-space:pre-wrap;word-break:break-word}' +
      'ins{background:rgba(90,160,90,.18);text-decoration:none}del{background:rgba(200,90,80,.18);text-decoration:none}' +
      '.align{font:13px ui-monospace,Menlo,monospace;letter-spacing:.12em}' +
      'ul{padding-left:1.1rem}li{margin-bottom:.3rem;color:var(--dim)}' +
      '</style></head><body>',
  );

  parts.push(`<h1>${esc(h.primary)}</h1>`);
  if (h.secondary) parts.push(`<p class="sub">${esc(h.secondary)}</p>`);
  parts.push(
    `<div class="meta">${esc(ctx.nameA)} &rarr; ${esc(ctx.nameB)} &middot; ${esc(ctx.dialect)} &middot; ${esc(
      ctx.generatedAt,
    )}<br>${s.cases} cases &middot; ${s.regressed} regressed &middot; ${s.errored} errored &middot; ${
      s.diverged
    } diverged &middot; ${s.fixed} fixed &middot; ${s.unchanged} unchanged &middot; ${s.dropped} dropped &middot; ${
      s.added
    } added<br>joined on ${esc(s.join.keyFunction)} &middot; ${s.join.exact} exact &middot; ${
      s.join.similarity
    } by similarity &middot; ${s.join.ambiguous} ambiguous</div>`,
  );

  const flips = Object.entries(s.flipsByScorer).filter(([, n]) => n > 0);
  if (flips.length > 0) {
    parts.push('<h2>What changed</h2><table><tr><th>check</th><th class="n">rows</th></tr>');
    for (const [id, n] of flips.sort((x, y) => y[1] - x[1])) {
      parts.push(`<tr><td>${esc(id)}</td><td class="n">${n}</td></tr>`);
    }
    parts.push('</table>');
  }

  const regressions = result.cases.filter((c) => c.verdict === 'regression');
  if (regressions.length > 0) {
    parts.push('<h2>Regressions</h2>');
    for (const c of regressions.slice(0, 200)) {
      parts.push(`<div class="case bad"><div class="id">${esc(c.id)}</div>`);
      parts.push(`<div class="why">${esc(c.headlineReason)}</div>`);
      if (c.trajectory?.alignment) {
        parts.push(`<div class="align">${esc(c.trajectory.alignment)}</div>`);
      }
      parts.push('<pre>');
      for (const part of buildDiff(c.pair.a?.outputText ?? '', c.pair.b?.outputText ?? '')) {
        const v = esc(part.value.slice(0, 4000));
        if (part.added) parts.push(`<ins>${v}</ins>`);
        else if (part.removed) parts.push(`<del>${v}</del>`);
        else parts.push(v.length > 200 ? `${v.slice(0, 100)}…${v.slice(-100)}` : v);
      }
      parts.push('</pre></div>');
    }
  }

  const diverged = result.cases.filter((c) => c.verdict === 'diverged' || c.verdict === 'diverged-args');
  if (diverged.length > 0) {
    parts.push('<h2>Diverged, but nothing broke</h2><ul>');
    for (const c of diverged.slice(0, 80)) {
      parts.push(`<li><span class="id">${esc(c.id)}</span> — ${esc(c.headlineReason)}</li>`);
    }
    parts.push('</ul>');
  }

  if (s.notes.length > 0) {
    parts.push('<h2>Notes</h2><ul>');
    for (const n of s.notes) parts.push(`<li>${esc(n)}</li>`);
    parts.push('</ul>');
  }

  parts.push('<h2>What this cannot tell you</h2><ul>');
  for (const line of LIMITS_COPY) parts.push(`<li>${line}</li>`);
  parts.push('</ul>');
  parts.push(`<h2>Generated by</h2><p class="why">Backslide ${VERSION}, entirely in the browser.</p>`);
  parts.push('</body></html>');
  return parts.join('');
}
